import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import {
  Field,
  formValueSelector,
  propTypes as formPropTypes,
} from 'redux-form'
import { lpForm } from 'lp-form'
import { PartnerAutoSuggestInput, ComboBox } from 'components'
import * as Types from 'types'
import { formatObjectsToIds, parseIdsToObjects, serializeOptions } from 'utils'
import { SubmitButton, ButtonArea } from 'lp-components'
import { withProps } from 'recompose'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'

const propTypes = {
  ...formPropTypes,
  partnerOptions: PropTypes.arrayOf(Types.searchSuggestion),
  schoolPartnerships: PropTypes.array,
  organizationPartnerships: PropTypes.array,
  visibleSchoolYearId: PropTypes.number.isRequired,
  fetchPartner: PropTypes.func.isRequired,
  selectedPartner: PropTypes.object,
}

function PartnerPartnershipForm({
  handleSubmit,
  submitting,
  partnerOptions,
  organizationId,
  schoolPartnerships,
  organizationPartnerships,
  visibleSchoolYearId,
  fetchPartner,
  selectedPartner,
}) {
  const isOrganizationSelected = Boolean(organizationId)

  useEffect(() => {
    if (visibleSchoolYearId && organizationId) {
      fetchPartner(organizationId, visibleSchoolYearId)
    }
  }, [visibleSchoolYearId, organizationId, fetchPartner])

  const selectedPartnerSchoolPartnerships =
    selectedPartner?.partnerships?.filter((p) => p.schoolId) || []

  const filteredSchoolPartnerships =
    selectedPartnerSchoolPartnerships.filter(
      (ps) => !schoolPartnerships.some((sp) => sp.schoolId === ps.schoolId)
    ) || []

  const selectedPartnerPrograms = selectedPartner?.programs || []

  const filteredProgramPartnerships =
    selectedPartnerPrograms.filter((program) => {
      const isProgramPartOfOrg = organizationPartnerships.some(
        (orgPartnership) =>
          orgPartnership.organizationId === organizationId &&
          orgPartnership.partnerPrograms.some(
            (partnerProgram) => partnerProgram.id === program.id
          )
      )

      return !isProgramPartOfOrg
    }) || []

  return (
    <form onSubmit={handleSubmit}>
      <Field
        name="organizationId"
        label="Enter a Partner Name"
        component={PartnerAutoSuggestInput}
        suggestions={partnerOptions}
        parse={(option) => (option ? option.id : null)}
      />

      {isOrganizationSelected && (
        <>
          <Field
            name="programs"
            label="Choose the programs that are associated with this Partner"
            component={ComboBox}
            options={serializeOptions(filteredProgramPartnerships, {
              key: 'name',
              value: 'id',
            })}
            isMulti={true}
            format={formatObjectsToIds('id')}
            parse={parseIdsToObjects('id')}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            blurInputOnSelect={false}
          />

          <Field
            name="schools"
            label="Choose the schools that are associated with this Partner"
            component={ComboBox}
            options={serializeOptions(filteredSchoolPartnerships, {
              key: 'schoolName',
              value: 'schoolId',
            })}
            isMulti={true}
            format={formatObjectsToIds('schoolId')}
            parse={parseIdsToObjects('schoolId')}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            blurInputOnSelect={false}
          />
        </>
      )}
      <ButtonArea>
        <SubmitButton {...{ submitting, style: 'secondary' }}>
          Add Partner
        </SubmitButton>
      </ButtonArea>
    </form>
  )
}

PartnerPartnershipForm.propTypes = propTypes

const selectFormValue = formValueSelector('partner-add-partnership')

function mapStateToProps(state) {
  return {
    organizationId: selectFormValue(state, 'organizationId'),
    selectedPartner: selectors.partner(state),
  }
}

const mapDispatchToProps = {
  fetchPartner: apiActions.fetchPartner,
}

export default compose(
  withProps(({ selectedPartner, partnerOptions }) => {
    return {
      beforeSubmit: ({ organizationId, programs, schools, ...values }) => {
        const fullPartner = partnerOptions?.find(
          (partner) => partner.id === organizationId
        )

        const programPartnerships = selectedPartner?.programs || []
        const fullPrograms = programs
          ? programs.map((program) => {
              const originalProgram = programPartnerships?.find(
                (p) => p.id === program.id
              )
              return originalProgram || program
            })
          : []

        return {
          ...values,
          partner: fullPartner,
          programs: fullPrograms,
          schoolIds: formatObjectsToIds('schoolId', schools),
        }
      },
    }
  }),
  lpForm({
    name: 'partner-add-partnership',
    constraints: {
      organizationId: {
        presence: { message: '^Partner selection cannot be blank' },
      },
    },
  }),
  connect(mapStateToProps, mapDispatchToProps)
)(PartnerPartnershipForm)
